import React, { useCallback, useEffect, useState } from 'react';
import api, { openSafeTelegramLink, isSafeTelegramUrl } from '../api/client';
import { CoinIcon, FloatIcon, ScreenTitle } from './CurrencyIcons';
import AlertModal from './AlertModal';
import Avatar from './Avatar';
import { iconUrl } from '../utils/assets';
import { useLocale } from '../i18n/LocaleContext';
import { useGameAlert } from '../hooks/useGameAlert';
import { switchInlineQuery } from '../utils/telegram';

const friendLabel = (friend) => {
  const raw = String(friend?.name || '').replace(/[\u200b-\u200f\u202a-\u202e\u2060-\u2064\ufeff]/g, '').trim();
  if (raw) return raw;
  if (friend?.username) return `@${friend.username}`;
  return `#${friend?.id || '—'}`;
};

const Referals = () => {
  const { t, lang } = useLocale();
  const { alert, closeAlert, showError, showInfo } = useGameAlert();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [giftAvailable, setGiftAvailable] = useState(null);
  const [gifting, setGifting] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [refs, eng] = await Promise.all([api.referrals(), api.engagement()]);
      setData(refs);
      setGiftAvailable(Boolean(eng.gift_available));
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    load();
  }, [load]);

  const share = () => {
    if (!data?.invite_link || !isSafeTelegramUrl(data.invite_link)) return;
    const q = data.invite_code ? `ref_${data.invite_code}` : '';
    if (q && switchInlineQuery(q, ['users'])) return;
    const text = encodeURIComponent(
      lang === 'en'
        ? 'Join me on Tap Mars — tap the Red Planet together!'
        : 'Залетай в Tap Mars и тапай по Красной планете вместе со мной!'
    );
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(data.invite_link)}&text=${text}`;
    openSafeTelegramLink(shareUrl);
  };

  const copyLink = async () => {
    if (!data?.invite_link) return;
    try {
      await navigator.clipboard.writeText(data.invite_link);
      showInfo({ icon: 'handshake.png', title: t('linkCopied'), confirmLabel: t('gotIt') });
      try {
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      } catch (_) {
        /* ignore */
      }
    } catch {
      showInfo({ title: data.invite_link, icon: 'bulb.png' });
    }
  };

  return (
    <main className="friends screen-enter">
      <div className="friends-title-row">
        <ScreenTitle
          icon={iconUrl('handshake.png')}
          title={t('friendsTitle')}
          subtitle={t('friendsSubtitle')}
        />
        <button
          type="button"
          className="friends-help-btn"
          aria-label={t('friendsHelpTitle')}
          onClick={() => setHelpOpen(true)}
        >
          <FloatIcon src={iconUrl('question.png')} size={18} eager animated native />
        </button>
      </div>

      {loading ? <div className="inline-message shimmer-line">{t('loading')}</div> : null}

      {data ? (
        <>
          <div className="invite-panel">
            <div className="invite-stats">
              <div className="invite-stat">
                <span className="invite-stat-val">{data.invite_count}</span>
                <span className="item-desc">{t('inviteCount')}</span>
              </div>
              <div className="invite-stat">
                <span className="invite-stat-val reward-line">
                  <CoinIcon size={16} /> +{data.reward_per_invite}
                </span>
                <span className="item-desc">{t('friends')}</span>
              </div>
              <div className="invite-stat">
                <span className="invite-stat-val">{data.active_cut_percent ?? 2}%</span>
                <span className="item-desc">%</span>
              </div>
            </div>
            <div className="invite-actions">
              <button type="button" className="action-btn" onClick={share}>
                {t('share')}
              </button>
              <button type="button" className="action-btn stars" onClick={copyLink}>
                {t('copyLink')}
              </button>
            </div>
          </div>

          <div className="section-label">{t('friends')}</div>
          {giftAvailable === false ? (
            <div className="inline-message">{t('giftUsed')}</div>
          ) : giftAvailable ? (
            <div className="item-desc" style={{ marginBottom: 8 }}>
              {t('giftHint')}
            </div>
          ) : null}
          <div className="frens-list">
            {(data.friends || []).length === 0 ? (
              <div className="inline-message">{t('inviteFriends')}</div>
            ) : (
              data.friends.map((friend) => (
                <div className="frens-item" key={friend.id}>
                  <div className="frens-item-left">
                    <Avatar
                      userData={{
                        photo_url: friend.photo_url,
                        first_name: friendLabel(friend),
                        username: friend.username,
                      }}
                      size={40}
                    />
                    <div className="item-text">
                      <span className="frens-item-info-name">{friendLabel(friend)}</span>
                      {friend.username ? (
                        <span className="item-desc">@{friend.username}</span>
                      ) : null}
                    </div>
                  </div>
                  <span className="frens-item-info-balance">
                    {giftAvailable ? (
                      <span className="gift-actions">
                        <button
                          type="button"
                          className="chip-tab"
                          disabled={gifting === friend.id}
                          onClick={async () => {
                            setGifting(friend.id);
                            try {
                              await api.sendGift(friend.id, 'energy');
                              setGiftAvailable(false);
                              showInfo({
                                icon: 'energy_new.png',
                                title: t('giftSent'),
                                confirmLabel: t('gotIt'),
                              });
                            } catch (e) {
                              showError(e);
                            } finally {
                              setGifting(null);
                            }
                          }}
                        >
                          {t('giftEnergy')}
                        </button>
                        <button
                          type="button"
                          className="chip-tab"
                          disabled={gifting === friend.id}
                          onClick={async () => {
                            setGifting(friend.id);
                            try {
                              await api.sendGift(friend.id, 'coins');
                              setGiftAvailable(false);
                              showInfo({
                                icon: 'coin.png',
                                title: t('giftSent'),
                                confirmLabel: t('gotIt'),
                              });
                            } catch (e) {
                              showError(e);
                            } finally {
                              setGifting(null);
                            }
                          }}
                        >
                          {t('giftCoins')}
                        </button>
                      </span>
                    ) : (
                      (friend.joined_at ? new Date(friend.joined_at).toLocaleDateString() : '·')
                    )}
                  </span>
                </div>
              ))
            )}
          </div>
        </>
      ) : null}

      <AlertModal
        open={helpOpen}
        icon="question.png"
        title={t('friendsHelpTitle')}
        onClose={() => setHelpOpen(false)}
        confirmLabel={t('gotIt')}
      >
        <div className="friends-help-body">{t('friendsHelpBody')}</div>
      </AlertModal>
      <AlertModal open={Boolean(alert)} {...(alert || {})} onClose={closeAlert} />
    </main>
  );
};

export default Referals;
